import {
  Box,
  Button,
  IconButton,
  Link,
  TextField,
  Typography,
  Chip,
  alpha,
  Paper,
  useTheme,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { genericFormProps } from '../signup.types';
import { useCallback, useState } from 'react';
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '../../../svg/DeleteIcon3';
import ResyncIcon from '../../../svg/ResyncIcon';
import { toast } from 'react-toastify';
import {
  GoogleDriveValidate,
  handleGoogleDriveResync,
} from '../../../utils/api';
import { useAuth } from '../../../hooks/useAuth';
import GoogleDriveIcon from '../../../svg/GoogleDriveIcon';
import PdfIcon from '../../../svg/PdfIcon';
import TXTIcon from '../../../svg/TXTIcon';
import DocxIcon from '../../../svg/DoucmentIcon';
import PptIcon from '../../../svg/PptIcon';
import FileIcon2 from '../../../svg/FileIcon2';
import { extractFolderId } from '../../../utils/helper';

const GoogleDriveForm: React.FC<genericFormProps> = (props) => {
  const { authUser } = useAuth();

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    formSubmitting,
    isValid,
    dirty,
    showSteps,
  } = props;

  const theme = useTheme();
  const [googleDriveLink, setGoogleDriveLink] = useState<string>('');
  const [gdriveErrors, setGdriveErrors] = useState<string>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<{
    [key: number]: boolean;
  }>({});
  const [resyncingItems, setResyncingItems] = useState<{
    [key: number]: boolean;
  }>({});
  const [fetchedFileDetails, setFetchedFileDetails] = useState<{
    [key: number]: {
      allowed_files: Array<{ name: string; size: number }>;
      skipped_files: Array<{ name: string; size: number }>;
      total_files: number;
      isLoading: boolean;
      error: string | null;
    };
  }>({});

  const checkIfFolderIsPublic = async (url: string): Promise<any> => {
    try {
      if (!authUser?.uid) {
        return null;
      }

      const validateLink = await GoogleDriveValidate(url);

      return validateLink;
    } catch (error) {
      return null;
    }
  };

  const handleAddLink = async () => {
    if (googleDriveLink) {
      setGdriveErrors(null);
      const validGoogleDriveUrl =
        /^(https:\/\/)?(drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=|drive\/folders\/)[\w-]+(?:\?.*)?)$/;

      if (!validGoogleDriveUrl.test(googleDriveLink)) {
        setGdriveErrors(
          'Invalid Google Drive link. Must start with https://drive.google.com/file/d/{file_id}, https://drive.google.com/open?id={file_id}, https://drive.google.com/uc?id={file_id}, or https://drive.google.com/drive/folders/{folder_id}'
        );
      } else {
        const isFolderLink = googleDriveLink.includes('/folders/');

        if (isFolderLink) {
          setIsCheckingAccess(true);

          const validateResponse = await checkIfFolderIsPublic(googleDriveLink);

          setIsCheckingAccess(false);

          if (!validateResponse?.is_public) {
            toast.warning(
              'The google drive link is not accessible. Please ensure the folder is set to public and try again.'
            );
            return;
          }

          if (!validateResponse?.is_allowed) {
            toast.error(
              `Maximum ${
                validateResponse?.max_allowed_files || 100
              } files allowed. This folder contains ${
                validateResponse?.total_files_in_gdrive
              } files.`
            );
            return;
          }

          let copy: any = [...values.google_drive_links];
          copy.push({
            url: googleDriveLink,
            allowed_files: validateResponse?.allowed_files || [],
            skipped_files: validateResponse?.skipped_files_due_to_size || [],
            total_files: validateResponse?.total_files_in_gdrive || 0,
          });
          setFieldValue('google_drive_links', copy);
          setGoogleDriveLink('');

          toast.success(
            `Successfully added ${
              validateResponse?.allowed_files?.length || 0
            } files from Google Drive`
          );
        } else {
          let copy = [...values.google_drive_links];
          copy.push({ url: googleDriveLink });
          setFieldValue('google_drive_links', copy);
          setGoogleDriveLink('');
        }
      }
    }
  };

  const handleResync = useCallback(
    async (document: any, index: number) => {
      if (!authUser?.uid) {
        toast.error('User ID is not available');
        return;
      }

      try {
        setResyncingItems((prev) => ({ ...prev, [index]: true }));
        const folderId = extractFolderId(document.url);


      if (folderId) {
          await handleGoogleDriveResync(authUser.uid, [folderId]);
        }

        toast.success('Resync started successfully');
      } catch (error) {
        toast.error('Failed to start resync. Please try again.');
      } finally {
        setResyncingItems((prev) => ({ ...prev, [index]: false }));
      }
    },
    [authUser, values.google_drive_links, setFieldValue]
  );

  const toggleExpand = async (index: number, document: any) => {
    const isCurrentlyExpanded = expandedItems[index] || false;
    
    if (!isCurrentlyExpanded && document?.is_indexed === true && !document?.allowed_files?.length) {
      if (!fetchedFileDetails[index] && !fetchedFileDetails[index]?.isLoading) {
        setFetchedFileDetails((prev) => ({
          ...prev,
          [index]: {
            allowed_files: [],
            skipped_files: [],
            total_files: 0,
            isLoading: true,
            error: null,
          },
        }));

        try {
          const validateResponse = await GoogleDriveValidate(document.url);
          
          if (validateResponse) {
            setFetchedFileDetails((prev) => ({
              ...prev,
              [index]: {
                allowed_files: validateResponse.allowed_files || [],
                skipped_files: validateResponse.skipped_files_due_to_size || [],
                total_files: validateResponse.total_files_in_gdrive || 0,
                isLoading: false,
                error: null,
              },
            }));
          } else {
            setFetchedFileDetails((prev) => ({
              ...prev,
              [index]: {
                ...prev[index],
                isLoading: false,
                error: 'Failed to fetch file details',
              },
            }));
          }
        } catch (error) {
          setFetchedFileDetails((prev) => ({
            ...prev,
            [index]: {
              ...prev[index],
              isLoading: false,
              error: 'Failed to fetch file details',
            },
          }));
        }
      }
    }

    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getFileIcon = useCallback(
    (fileName: string) => {
      const ext = fileName?.split('.').pop()?.toLowerCase();

      if (ext === 'pdf') {
        return <PdfIcon width={20} height={20} />;
      }

      if (ext === 'txt') {
        return <TXTIcon width={20} height={20} />;
      }

      if (ext === 'docx' || ext === 'doc') {
        return <DocxIcon width={20} height={20} />;
      }

      if (ext === 'pptx' || ext === 'ppt') {
        return <PptIcon width={20} height={20} />;
      }

      return (
        <FileIcon2 width={20} height={20} fill={theme.palette.text.secondary} />
      );
    },
    [theme]
  );

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  interface GoogleDriveDocument {
    url: string;
    is_indexed?: boolean | null;
    indexed_files_count?: number;
    llama_file_id?: string[];
    allowed_files?: Array<{ name: string; size: number }>;
    total_files?: number;
    indexed_on?: {
      toDate?: () => Date;
    };
    skipped_files?: Array<{ name: string; size: number }>;
  }

  interface StatusInfo {
    text: string;
    color: string;
    showProgress: boolean;
    icon: React.ReactElement | null;
    completedCount: number;
    totalCount: number;
    date: string | null;
  }

  const getStatusInfo = useCallback((document: GoogleDriveDocument): StatusInfo => {
      const completedCount = document.indexed_files_count ?? document.llama_file_id?.length ?? 0;
      const totalCount = document.allowed_files?.length ?? document.total_files ?? completedCount;
      
      const indexedDate = document.indexed_on?.toDate?.();
      const dateStr = indexedDate ? indexedDate.toLocaleDateString('en-US', { 
        month: 'numeric', 
        day: 'numeric', 
        year: 'numeric' 
      }) : '';

      if (document.is_indexed === null || document.is_indexed === undefined) {
        return {
          text: 'In progress...',
          color: 'primary.main',
          showProgress: true,
          icon: null,
          completedCount: completedCount,
          totalCount: totalCount,
          date: null,
        };
      }
      
      if (document.is_indexed === false) {
        return {
          text: 'Failed to process',
          color: 'error.main',
          showProgress: false,
          icon: <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />,
          completedCount: completedCount,
          totalCount: totalCount,
          date: dateStr,
        };
      }
      
      return {
        text: 'Processed successfully',
        color: 'success.main',
        showProgress: false,
        icon: <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />,
        completedCount: completedCount,
        totalCount: totalCount,
        date: dateStr,
      };
    }, []);

  const getFileCount = useCallback((document: GoogleDriveDocument): number => {
    if (document?.is_indexed === true) {
      return document?.indexed_files_count ?? 
             document?.llama_file_id?.length ?? 
             document?.allowed_files?.length ?? 
             document?.total_files ?? 
             0;
    }
    return document?.allowed_files?.length ?? 
           document?.total_files ?? 
           document?.indexed_files_count ?? 
           document?.llama_file_id?.length ?? 
           0;
  }, []);

  const renderDriveListItem = useCallback(
    (document: any, index: number, onDeleteHandler: () => void) => {
      const isIndexed = document?.hasOwnProperty('is_indexed')
        ? document.is_indexed
        : null;
      const isNewLink = !document?.hasOwnProperty('is_indexed');
      const isFolderLink = document?.url?.includes('/folders/');
      
      const fetchedDetails = fetchedFileDetails[index];
      
      const displayFiles = document?.allowed_files?.length > 0 
        ? document.allowed_files 
        : fetchedDetails?.allowed_files || [];
      const displaySkippedFiles = document?.skipped_files?.length > 0
        ? document.skipped_files
        : fetchedDetails?.skipped_files || [];
      
      const hasFileDetails = displayFiles.length > 0;
      const isLoadingFiles = fetchedDetails?.isLoading || false;
      
      const fileCount = getFileCount(document) || fetchedDetails?.total_files || displayFiles.length || 0;
      const status = getStatusInfo(document);
      const isExpanded = expandedItems[index] || false;
      const isResyncing = resyncingItems[index] || false;

      const showExpandButton = isFolderLink;

      return (
        <Box
          key={`drivelink-${index}`}
          display='flex'
          flexDirection='column'
          borderBottom={1}
          borderColor='divider'
          px={2.5}
          py={1.5}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}
            >
              <GoogleDriveIcon width={24} height={24} />

              <Box sx={{ flex: 1 }}>
                <Link
                  underline='hover'
                  href={`${document.url}`}
                  rel='noreferrer'
                  target='_blank'
                  sx={{
                    color: isIndexed === false ? 'error.main' : 'text.primary',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      wordBreak: 'break-word',
                    }}
                  >
                    {document.url}
                  </Typography>
                </Link>
                <Box display='flex' alignItems='center' gap={1} mt={0.5} flexWrap='wrap'>
                  {status.showProgress ? (
                    <>
                      <CircularProgress size={14} sx={{ color: status.color }} />
                      <Typography variant='caption' sx={{ color: status.color }}>
                        {status.text}
                      </Typography>
                    </>
                  ) : (
                    <>
                      {status.icon}
                      <Typography variant='caption' sx={{ color: status.color }}>
                        {status.text}
                      </Typography>
                    </>
                  )}
                  {status.totalCount > 0 && (
                    <>
                      <Typography variant='caption' color='text.secondary'>
                        •
                      </Typography>
                      <Typography 
                        variant='caption' 
                        sx={{ 
                          color: status.completedCount === status.totalCount 
                            ? 'success.main' 
                            : 'text.secondary' 
                        }}
                      >
                        {status.completedCount}/{status.totalCount} Synced
                      </Typography>
                    </>
                  )}
                  
                  {status.date && (
                    <>
                      <Typography variant='caption' color='text.secondary'>
                        •
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {status.date}
                      </Typography>
                    </>
                  )}
                </Box>
                
                {fileCount > 0 && (
                  <Box display='flex' alignItems='center' gap={1} mt={0.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {fileCount} file
                      {fileCount !== 1 ? 's' : ''}
                    </Typography>
                    {displaySkippedFiles.length > 0 && (
                        <Typography variant='caption' color='warning.main'>
                          ({displaySkippedFiles.length} skipped)
                        </Typography>
                      )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {showExpandButton && (
                <IconButton
                  size='small'
                  onClick={() => toggleExpand(index, document)}
                  disabled={isLoadingFiles}
                  sx={{
                    width: '30px',
                    height: '30px',
                  }}
                >
                  {isLoadingFiles ? (
                    <CircularProgress size={16} />
                  ) : isExpanded ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              )}

              {isIndexed && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    cursor: isResyncing ? 'not-allowed' : 'pointer',
                    borderRadius: 1,
                    opacity: isResyncing ? 0.6 : 1,
                    '&:hover': {
                      bgcolor: isResyncing ? 'transparent' : 'action.hover',
                    },
                  }}
                  onClick={() => !isResyncing && handleResync(document, index)}
                >
                  {isResyncing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ResyncIcon
                      width={24}
                      height={24}
                      style={{ opacity: 0.56 }}
                    />
                  )}
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '15.6px',
                      letterSpacing: '0.17px',
                    }}
                  >
                    {isResyncing ? 'Resyncing...' : 'Resync'}
                  </Typography>
                </Box>
              )}

              <IconButton
                id={`button-delete-${document.url}-${index}`}
                size='small'
                sx={{
                  width: '30px',
                  height: '30px',
                }}
                disabled={!isIndexed && !isNewLink}
                onClick={onDeleteHandler}
              >
                <DeleteIcon
                  stroke={
                    isIndexed || isNewLink
                      ? theme.palette['nav'].iconactive
                      : theme.palette.action.disabled
                  }
                />
              </IconButton>
            </Box>
          </Box>

          {showExpandButton && (
            <Collapse in={isExpanded}>
              <Box
                sx={{
                  mt: 2,
                  ml: 4,
                  pl: 2,
                }}
              >
                {isLoadingFiles ? (
                  <Box display='flex' alignItems='center' gap={1} py={2}>
                    <CircularProgress size={16} />
                    <Typography variant='caption' color='text.secondary'>
                      Loading files...
                    </Typography>
                  </Box>
                ) : fetchedDetails?.error ? (
                  <Typography variant='caption' color='error.main'>
                    {fetchedDetails.error}
                  </Typography>
                ) : hasFileDetails ? (
                  <>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        fontWeight: 600,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Files in folder:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {displayFiles.map(
                        (file: any, fileIndex: number) => (
                          <Box
                            key={`file-${index}-${fileIndex}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.background.paper, 0.5),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                              },
                            }}
                          >
                            {getFileIcon(file.name)}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontSize: '0.875rem',
                                  color: 'text.primary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {file.name}
                              </Typography>
                            </Box>
                            {file.size && (
                              <Typography
                                variant='caption'
                                sx={{
                                  color: 'text.secondary',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatFileSize(file.size)}
                              </Typography>
                            )}
                          </Box>
                        )
                      )}

                      {displaySkippedFiles.length > 0 && (
                        <>
                          <Typography
                            variant='caption'
                            color='warning.main'
                            sx={{
                              fontWeight: 600,
                              display: 'block',
                              mt: 1,
                              mb: 0.5,
                            }}
                          >
                            Skipped files (too large):
                          </Typography>
                          {displaySkippedFiles.map(
                            (file: any, fileIndex: number) => (
                              <Box
                                key={`skipped-${index}-${fileIndex}`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                                  opacity: 0.7,
                                }}
                              >
                                {getFileIcon(file.name)}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant='body2'
                                    sx={{
                                      fontSize: '0.875rem',
                                      color: 'warning.main',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {file.name}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant='caption'
                                  sx={{
                                    color: 'warning.main',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            )
                          )}
                        </>
                      )}
                    </Box>
                  </>
                ) : (
                  <Typography variant='caption' color='text.secondary'>
                    No files found in this folder
                  </Typography>
                )}
              </Box>
            </Collapse>
          )}
        </Box>
      );
    },
    [theme, expandedItems, resyncingItems, fetchedFileDetails, getFileIcon, handleResync, getStatusInfo, getFileCount]
  );

  const hasGoogleDriveLinks = !!values.google_drive_links.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Google Drive Links
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          Connect your Google Drive folders to train your AI persona
        </Typography>
         <Typography
              sx={{
                  color: 'primary.secondary',
                  fontSize: 16,
                  fontFamily: '"Merriweather Sans", sans-serif',
                  fontWeight: 600,
                  lineHeight: '20.8px',
                  letterSpacing: '0.15px',
                  mb: 2,
                  mt: 2,
                }}
            >
               *Optional
            </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background['container-2'],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <GoogleIcon sx={{ color: '#4285F4' }} />
            <Typography variant='h7'>Add Google Drive Links</Typography>
          </Box>
          <Typography variant='body2'>
            Add links to your Google Drive folders. <strong>Note:</strong>{' '}
            Folders must be set to public to be synced and used for training.
          </Typography>
        </Box>

        {/* Google Drive Link Input */}
        <TextField
          type='string'
          fullWidth
          name='google_drive_links'
          margin='none'
          label='Add Google Drive Link'
          placeholder='https://drive.google.com/drive/folders/...'
          value={googleDriveLink}
          onBlur={handleBlur}
          onChange={(e) => {
            setGoogleDriveLink(e.target.value);
          }}
          error={Boolean(gdriveErrors)}
          helperText={gdriveErrors}
          autoComplete='off'
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        <Button
          variant='contained'
          size='large'
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            fontWeight: 600,
            boxShadow: theme.shadows[4],
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: theme.shadows[8],
              transform: 'translateY(-2px)',
            },
          }}
          onClick={handleAddLink}
          disabled={!googleDriveLink || isCheckingAccess}
        >
          {isCheckingAccess ? 'Checking...' : 'Add Google Drive Link'}
        </Button>
      </Paper>

      {/* Google Drive Links List Section */}
      {hasGoogleDriveLinks && (
        <Box mt={1}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: theme.palette.background['container-2'],
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <GoogleIcon sx={{ color: '#4285F4' }} />
              <Typography variant='h7'>My Google Drive Links</Typography>
            </Box>
            <Chip
              label={`${values.google_drive_links.length} ${
                values.google_drive_links.length === 1 ? 'folder' : 'folders'
              }`}
              size='small'
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          </Paper>

          <Box>
            {(values?.google_drive_links || []).map(
              (document: any, index: number) =>
                renderDriveListItem(document, index, () => {
                  let links = [...(values.google_drive_links || [])];
                  links?.splice(index, 1);
                  setFieldValue('google_drive_links', links || null);
                })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GoogleDriveForm;